import { Pipe, PipeTransform } from '@angular/core';
import {
  add,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  isBefore,
} from 'date-fns';
import { Observable, of, timer } from 'rxjs';
import { map } from 'rxjs/operators';

const countdownObservables: Record<string, Observable<string>> = {};

const getCountDown$ = (
  dateAsString: string,
  parts: possibleParts[] = ['month', 'day', 'hour', 'minute', 'second'],
): Observable<string> => {
  try {
    const asMoment = new Date(dateAsString);
    return timer(0, 200).pipe(
      map(() => {
        const preciseDiff: PreciseDiff = preciseDifference(asMoment, parts);
        if (preciseDiff === null) {
          return '–';
        }
        const result = [];
        let dateOrderIndex = 0;
        while (dateOrderIndex < dateOrder.length && result.length < 4) {
          const key = dateOrder[dateOrderIndex];
          if ((preciseDiff[key] as number) > 0 || result.length === 1) {
            const keyName = substitutes[key] ? substitutes[key] : key.substring(0, 1).toLocaleLowerCase();
            result.push(`${preciseDiff[key]}${keyName}`);
          }
          dateOrderIndex++;
        }
        if (result.length) {
          return `${result.join(' ')}`;
        }
        return '–';
      }),
    );
  } catch (e) {
    console.error(e);
  }
  return of('–');
};

const preciseDifference = (
  date: Date,
  parts: possibleParts[] = ['month', 'day', 'hour', 'minute', 'second'],
): PreciseDiff | null => {
  let now = new Date();
  if (isBefore(date, now)) {
    return null;
  }
  const response: PreciseDiff = {
    seconds: 0,
    minutes: 0,
    hours: 0,
    days: 0,
    months: 0,
    years: 0,
    firstDateWasLater: false,
  };

  parts.forEach((part) => {
    const fn = differenceInFn[part];
    if (fn) {
      const differenceInPart = fn(date, now);
      if (differenceInPart) {
        response[`${part}s`] = differenceInPart;
        const additionDuration = { [`${part}s`]: differenceInPart };
        // We are just going to subtract from "now" until we get down to the date
        now = add(now, additionDuration);
      }
    }
  });
  return response;
};

@Pipe({
  name: 'countdown',
  standalone: false,
})
export class CountdownPipe implements PipeTransform {
  // @ts-ignore
  public transform(dateInput: string | Date, parts: possibleParts[] = ['month', 'day', 'hour', 'minute', 'second']) {
    const dateString = new Date(dateInput).toString();
    if (!countdownObservables[dateString]) {
      countdownObservables[dateString] = getCountDown$(dateString, parts);
    }
    return countdownObservables[dateString];
  }
}

interface PreciseDiff {
  days: number;
  firstDateWasLater: boolean;
  hours: number;
  minutes: number;
  months: number;
  seconds: number;
  years: number;
}

const dateOrder: (keyof PreciseDiff)[] = ['years', 'months', 'days', 'hours', 'minutes', 'seconds'];
const substitutes: Partial<Record<keyof PreciseDiff, string>> = { months: 'mo' };

const differenceInFn = {
  second: differenceInSeconds,
  minute: differenceInMinutes,
  hour: differenceInHours,
  day: differenceInDays,
  month: differenceInMonths,
};
// eslint-disable-next-line @typescript-eslint/naming-convention
type possibleParts = keyof typeof differenceInFn;
